import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { CashBack } from '@lt/components';
import { useTranslation } from '@lt/localization';
import HotelRating from 'src/components/HotelRating';
import { useForceRender } from 'src/hooks/useForceRender';
import { getIsHotelOnly, getIsMobile } from 'src/store/view/selectors';
import { sendPageviewAction } from 'src/store/analytics/actions';
import { showPageAction } from '../../store/actions/view';
import { getHotelRoulette, getLinkedHotels } from '../../store/selectors/hotel';
import { getSearchParamsForUrl } from '../../store/selectors/searchParams';
import {
  getHotelCurrentPage,
  getIsWhitelabel,
} from '../../store/selectors/view';

import HintFactory from '../../../../components/Hint/HintFactory';
import { isSSR } from '../../../../helpers/ssr';
import Container from '../../components/Container';
import HotelRelink from '../../components/HotelRelink';
import MobilePage from '../../components/MobilePage';
import Paper from '../../components/Paper';
import {
  COMPONENT_ID,
  HOTEL_MOBILE_PAGES,
  HOTEL_SEARCHER_FIXED_BUTTON_HEIGHT,
} from '../../constants';
import {
  getCashbackForCheapestRoom,
  getCashbackTypeForCheapestRoom,
} from '../../store/selectors/rooms';
import ExperienceContainer from '../ExperienceContainer';
import ExtraItemsContainer from '../ExtraItemsContainer';
import FacilitiesContainer from '../FacilitiesContainer';
import FortunaJumbotronContainer from '../FortunaJumbotronContainer';
import Gallery from '../Gallery';
import HeaderContainer from '../Header';
import HotelInfo from '../HotelInfo';
import HotelMapContainer from '../HotelMap';
import RatingBadge from '../RatingBadge';
import ReviewsDesktop from '../Reviews/ReviewsDesktop';
import SearcherFixedButtonContainer from '../SearcherFixedButton';
import IncludedServicesHint from './IncludedServicesHint';

const LayoutMobile = (props) => {
  const {
    page,
    showPage,
    isRoulette,
    linkedHotels,
    searchParams,
    isWhitelabel,
    cashbackCount,
    cashbackType,
    isHotelOnly,
    isMobile,
    sendPageview,
  } = props;
  const { t } = useTranslation();
  useEffect(() => {
    switch (page) {
      case 'searcher':
        sendPageview(
          isHotelOnly
            ? 'hotel_room_offers_hotels_only'
            : 'hotel_room_offers_tours',
        );
        break;
      case 'reviews':
        sendPageview(
          isHotelOnly ? 'hotel_reviews_hotels_only' : 'hotel_reviews_tours',
        );
        break;
      default:
    }
  }, [page, sendPageview, isHotelOnly]);
  const isSearcher = page === HOTEL_MOBILE_PAGES.SEARCHER;
  const isMap = page === HOTEL_MOBILE_PAGES.MAP;
  const isFixedButtonVisible = !(isSearcher || isMap);

  const { countForceRender } = useForceRender();

  return (
    <StyledContainer
      className="hotel-mobile"
      paddingBottom={isFixedButtonVisible}
    >
      <HintFactory autoPosition events attribute="data-text-hint" isMobile />
      <IncludedServicesHint isMobile />
      {page ? (
        <MobilePage
          goBack={() => showPage(null)}
          page={page}
          isFullscreen={isMap}
        />
      ) : (
        <div>
          <Container isMobile white marginBottom={0}>
            <HeaderContainer isHotelOnly={isHotelOnly} />
          </Container>
          <Container isMobile fullWidth>
            {isRoulette ? (
              <FortunaJumbotronContainer isMobile />
            ) : (
              <>
                {!isWhitelabel && cashbackCount > 0 && (
                  <StyledCashback
                    count={`+${cashbackCount}`}
                    size="s"
                    isMobile
                    type={cashbackType}
                    cashbackText
                  />
                )}
                <RatingBadge isMobile />
                <Gallery />
              </>
            )}
          </Container>
          <Container isMobile fullWidth id={COMPONENT_ID.EXPERIENCE}>
            <ExperienceContainer isMobile fullWidth />
          </Container>
          {isRoulette ? (
            <Container isMobile>
              <Paper>
                <ExtraItemsContainer />
              </Paper>
            </Container>
          ) : (
            <HotelInfo />
          )}
          <Container isMobile>
            <StyledHotelMapContainer
              isMobile
              lazyInit
              onExpand={() => showPage(HOTEL_MOBILE_PAGES.MAP)}
              interactive={false}
              height={200}
            />
          </Container>
          <>
            <HotelRating key={countForceRender} />
          </>
          {!isRoulette && (
            <FacilitiesContainer key={countForceRender} isMobile />
          )}
          {!isWhitelabel && linkedHotels.length > 0 && (
            <HotelRelinkContainer isMobile white>
              <HotelRelinkHeading>
                {t('hotel.similarHotels')}
              </HotelRelinkHeading>
              <HotelRelink hotels={linkedHotels} searchParams={searchParams} />
            </HotelRelinkContainer>
          )}
        </div>
      )}

      {isFixedButtonVisible && (
        <SearcherFixedButtonContainer isHotelOnly={isHotelOnly} />
      )}

      {isSSR && !isMobile && (
        <div style={{ display: 'none' }}>
          <ReviewsDesktop />
        </div>
      )}
    </StyledContainer>
  );
};

LayoutMobile.propTypes = {
  showPage: PropTypes.func.isRequired,
  page: PropTypes.string,
  isRoulette: PropTypes.bool.isRequired,
  cashbackCount: PropTypes.number,
  cashbackType: PropTypes.string,
};

const StyledCashback = styled(CashBack)`
  position: absolute;
  z-index: 2;
  left: 4px;
  top: 4px;
`;

const StyledContainer = styled.div`
  position: relative;
  ${(props) =>
    props.paddingBottom &&
    `
    padding-bottom: ${HOTEL_SEARCHER_FIXED_BUTTON_HEIGHT}px;
  `}
`;

const StyledHotelMapContainer = styled(HotelMapContainer)`
  z-index: 1;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.borderRadiusBig};
`;

const HotelRelinkHeading = styled.div`
  font-weight: bold;
  font-size: 18px;
  line-height: 22px;
  margin-bottom: 18px;
`;

const HotelRelinkContainer = styled(Container)`
  padding-top: 16px;
  padding-bottom: 10px;
`;

const mapStateToProps = (state) => ({
  page: getHotelCurrentPage(state),
  isRoulette: getHotelRoulette(state),
  linkedHotels: getLinkedHotels(state),
  searchParams: getSearchParamsForUrl(state),
  isWhitelabel: getIsWhitelabel(state),
  cashbackCount: getCashbackForCheapestRoom(state),
  cashbackType: getCashbackTypeForCheapestRoom(state),
  isHotelOnly: getIsHotelOnly(state),
  isMobile: getIsMobile(state),
});

const mapDispatchToProps = {
  showPage: showPageAction,
  sendPageview: sendPageviewAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutMobile);
